import { useEffect, useRef, useState, useCallback } from 'react';

const slides = [
  {
    id: 1,
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/49a6dbae0761ef1a6313a43d481392e0.jpeg',
    alt: 'Fluido de perforación en mezcladora industrial',
    caption: 'Preparación de Fluidos de Perforación',
    sub: 'Mezcla y homogeneización en campo',
  },
  {
    id: 2,
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/09a86a442f8e6b72b5a82f432cae27bc.jpeg',
    alt: 'Muestras de crudo en horno de laboratorio numeradas',
    caption: 'Ensayos de Control de Calidad',
    sub: 'Pruebas en laboratorio especializado',
  },
  {
    id: 3,
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/630f658930f63712dba0d8079ce891f0.jpeg',
    alt: 'Separación crudo-agua en frasco BOROSIL',
    caption: 'Proceso de Demulsificación',
    sub: 'Separación efectiva crudo–agua con ALPHA-29',
  },
  {
    id: 4,
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/3402b7a8525f0e33e52ff7c50674d5df.jpeg',
    alt: 'Fluido de perforación cremoso en mezcladora',
    caption: 'Fluidos de Perforación en Proceso',
    sub: 'Aplicación de aditivos en operaciones activas',
  },
  {
    id: 5,
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/46c578258131142314943d7342e85be1.jpeg',
    alt: 'Muestras de productos químicos en laboratorio',
    caption: 'Línea de Productos Químicos',
    sub: 'Formulaciones especializadas para la industria petrolera',
  },
];

export default function Gallery() {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % slides.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + slides.length) % slides.length);
  }, []);

  // Auto-advance every 2 seconds
  useEffect(() => {
    if (paused || lightbox !== null) return;
    intervalRef.current = setInterval(next, 2000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [next, paused, lightbox]);

  const goTo = (idx: number) => {
    setCurrent(idx);
    // Reset timer
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-background-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary-500 text-sm font-600 tracking-widest uppercase mb-3 block">
            Galería
          </span>
          <h2 className="font-heading font-700 text-4xl md:text-5xl text-foreground-950 mb-4">
            Trabajo en <span className="text-primary-500">Campo y Laboratorio</span>
          </h2>
          <div className="section-divider mx-auto"></div>
        </div>

        {/* Carousel */}
        <div
          className={`relative rounded-2xl overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Slides */}
          <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
            {slides.map((slide, i) => (
              <div
                key={slide.id}
                className={`absolute inset-0 transition-opacity duration-700 ${i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
              >
                <img
                  src={slide.src}
                  alt={slide.alt}
                  className="w-full h-full object-cover object-center cursor-pointer"
                  onClick={() => setLightbox(i)}
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />

                {/* Caption */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 pointer-events-none">
                  <div className={`transition-all duration-500 ${i === current ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                    <span className="text-accent-400 text-xs font-600 tracking-widest uppercase block mb-1">
                      Poly Alphas Chemicals C.A.
                    </span>
                    <h3 className="font-heading font-700 text-2xl md:text-3xl text-white leading-tight mb-1">
                      {slide.caption}
                    </h3>
                    <p className="text-white/70 text-sm md:text-base">{slide.sub}</p>
                  </div>
                </div>

                {/* Zoom hint */}
                <div className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm pointer-events-none">
                  <i className="ri-zoom-in-line text-white text-sm"></i>
                </div>
              </div>
            ))}
          </div>

          {/* Prev / Next arrows */}
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/60 text-white transition-all duration-200 cursor-pointer backdrop-blur-sm"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/60 text-white transition-all duration-200 cursor-pointer backdrop-blur-sm"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>

          {/* Dot indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); goTo(i); }}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  i === current
                    ? 'w-6 h-2.5 bg-primary-500'
                    : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/70'
                }`}
              />
            ))}
          </div>

          {/* Slide counter */}
          <div className="absolute top-4 left-4 z-20 px-3 py-1 rounded-full bg-black/30 backdrop-blur-sm text-white text-xs font-600">
            {current + 1} / {slides.length}
          </div>

          {/* Pause indicator */}
          {paused && (
            <div className="absolute top-4 right-14 z-20 px-3 py-1 rounded-full bg-black/30 backdrop-blur-sm text-white/60 text-xs flex items-center gap-1.5">
              <i className="ri-pause-line text-xs"></i>
              Pausado
            </div>
          )}
        </div>

        {/* Thumbnail strip */}
        <div className={`flex gap-3 mt-4 justify-center transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              onClick={() => goTo(i)}
              className={`relative w-16 h-12 md:w-20 md:h-14 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer transition-all duration-200 ${
                i === current
                  ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-background-50'
                  : 'opacity-60 hover:opacity-100'
              }`}
            >
              <img src={slide.src} alt={slide.alt} className="w-full h-full object-cover object-center" />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer hover:bg-white/20 transition-colors z-10"
            onClick={() => setLightbox(null)}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <img
            src={slides[lightbox].src}
            alt={slides[lightbox].alt}
            className="max-w-4xl w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm text-center">
            {slides[lightbox].caption}
          </p>
        </div>
      )}
    </section>
  );
}